/* eslint-disable max-lines-per-function */
import { Check, Plus, Star06 } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ReactSelect, { components, NoticeProps, MenuPlacement } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import verifiedIcon from 'src/assets/img/verified-tick.svg';
import { COMMON_CONSTANTS } from 'src/constants/common';
import { setTooltipGlobalOpen } from 'src/redux/basic.slice';
import { getShortName, onError } from 'src/utils/CommonFunctions';

import ActionTooltip from '../ActionTooltip';
import CheckBox from '../CheckBox';
import ErrorMsg from '../errorMsg';
import TruncateTextCmp from '../TruncateText';

import { ISelectBoxProps } from './SelectBox.interface';

// Selected value with hover-to-reveal, opted into via `isShowTooltipOnTruncate`.
// TruncateText measures the node and only renders a tooltip once the text actually
// overflows, so a short value ("FTL") stays tooltip-free.
// Declared at MODULE scope on purpose: a component defined inside SelectBox would get a new
// identity on every parent render, remounting this node and resetting TruncateText's
// measurement state — which makes the tooltip flicker on each render.
const TruncatedSingleValue = (props: any) => {
    const valueRef = useRef<HTMLSpanElement>(null);
    const data = props?.data ?? {};
    const label = typeof data.label === 'string' ? data.label : '';
    // Rich options (icon / image / sub-label / …) are rendered by formatOptionLabel, which puts
    // its own `.truncate` span around the text. That inner span clips BEFORE any wrapper here
    // overflows, so the measurement would always read "not truncated" and no tooltip would ever
    // appear — which is exactly why wrapping props.children doesn't work. Those fall through to
    // the stock renderer; plain text labels are re-rendered below so the MEASURED node is the
    // node that actually truncates.
    const isPlainLabel = Boolean(label) && !data.icon && !data.image && !data.isImage && !data.customLabel && !data.subLabel && !data.email && !data.datetime;

    if (!isPlainLabel) return <components.SingleValue {...props}>{props.children}</components.SingleValue>;

    return (
        <components.SingleValue {...props}>
            {/* w-full, never w-max: TruncateText re-wraps this node once it measures as truncated,
                and a max-content wrapper would let the span size to the full text, measure as NOT
                truncated, unwrap, and oscillate. A full-width wrapper keeps the measurement stable. */}
            <TruncateTextCmp message={label} refName={valueRef} parentClassName="w-full" truncateTextParentClassName="block w-full">
                <span ref={valueRef} className="block truncate font-medium">
                    {label}
                </span>
            </TruncateTextCmp>
        </components.SingleValue>
    );
};

const SelectBox = ({
    errorText = '',
    label = '',
    options,
    menuPlaceOption = 'auto',
    noOptionMessage = 'No Option',
    hideNoOptionsMessage = false,
    classNamePrefix = 'select',
    tabIndex,
    isSearchable = false,
    isMultipleSelection,
    isDisableCheckbox,
    labelClassName = '',
    imageClassName,
    isDisabled,
    isError,
    isReadOnly,
    value,
    defaultValue,
    onChangeFunc,
    onInputChange,
    isClearable,
    isLoading,
    placeholder,
    classComp = '',
    dropdownImageClassName,
    selectValueWrapClass,
    selectLabelWrapClass,
    selectValueClass,
    isCloseOnScroll = true,
    icon,
    size,
    parentClassName,
    menuIsOpen,
    selectoptiontext,
    required,
    onMenuOpen,
    onMenuClose,
    placeholderIcon,
    isShowAiStar,
    labelRight,
    labelWrapClassName,
    selectOptionSubLabelClass,
    selectInputSubLabelClass,
    inlineSelect = false,
    showCustomerImage = false,
    autoCompleteSelect = false,
    onCreateOption,
    createOptionText,
    isValidNewOption = true,
    isHideHoverWithCreateOption = false,
    isOptionLoading = false,
    selectoptionWraptext,
    selectValueMainClass,
    isMultipleEquipmentType,
    closeMenuOnSelect,
    maxVisibleValues = 2,
    SelectDropdownWrapClass,
    onClearFunc,
    isShowErrorMsg = true,
    isShowLoadingSearchText = false,
    loadingSearchText = 'Searching',
    menuPosition = 'absolute',
    secondaryLabel,
    forceSearchable = false,
    maintainFocusOnClear = false,
    maxInputSearchLength = 80,
    isMenuOpenManually = true,
    isPreventTabPress = false,
    isPreventKeyboardSelect = false,
    isNotShowImage = false,
    selectWrapClass = '',
    defaultValueInput = '',
    isShowInputValue = false,
    keepMenuOpenOnMultiSelect = false,
    isNewOption = false,
    newOptionText,
    newOptionPosition = 'first',
    handleCreate,
    pagination = {
        isPagination: false,
        totalRecords: 0,
        currentLimit: 0,
        skipApiCallRef: { current: false },
    },
    createOptionPosition = 'last',
    formatCreateLabelIcon,
    formatCreateLabelText,
    formatCreateLabelTextClass,
    formatCreateLabelValue,
    formatCreateLabelDefaultShowText,
    isHideDropdownIndicator = false,
    truncateToOneLine = false,
    isHideCheckbox = false,
    isShowTooltipOnTruncate = false,
    isStableOptionRender = false,
    onKeyDown: customOnKeyDown,
}: ISelectBoxProps) => {
    const { t } = useTranslation();
    const selectRef = useRef<any>(null);
    /**
     * True only for the moment a multi-select pick is being committed.
     *
     * Clicking an option focuses then immediately blurs the input, and react-select answers that
     * blur with onMenuClose. handleMenuClose then force-closes (isCloseOnScroll defaults to true),
     * so the menu UNMOUNTS and the refocus in handleChange rebuilds it a tick later — every row of
     * the dropdown is destroyed and recreated on every pick, which is what the flicker is. This
     * flag lets handleMenuClose/handleBlur ignore that one self-inflicted close. Real dismissals —
     * escape, tab, outside click, scroll — happen while it is false and still close normally.
     */
    const isCommittingSelectionRef = useRef(false);
    const [menuIsOpenList, setMenuIsOpenList] = useState(menuIsOpen);
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState(defaultValueInput);
    const dispatch = useDispatch();
    const [maxVisibleValue, setMaxVisibleValue] = useState(maxVisibleValues);

    const [hasUsedManualFix, setHasUsedManualFix] = useState(false);
    const [placement, setPlacement] = useState<MenuPlacement>(menuPlaceOption);

    // Memoize the Plus icon to avoid recreating on every render
    const plusIcon = React.useMemo(() => <Plus className="w-4 h-4 text-primary flex-none" />, []);

    // Helper function to check if option is "Add New"
    const checkIsAddNewOption = React.useCallback(
        (option: any) => option?.value === '__add_new__' || option?.isAddNewOption === true || option?.data?.value === '__add_new__' || option?.data?.isAddNewOption === true,
        []
    );

    // Create "Add New" option when isNewOption is true
    const addNewOption = React.useMemo(() => {
        if (!isNewOption) return null;

        return {
            value: '__add_new__',
            label: newOptionText || 'Add New',
            isNewOption: true,
            isAddNewOption: true,
            icon: plusIcon,
        };
    }, [isNewOption, newOptionText, plusIcon]);

    // Process options to sort new options to the top (or bottom, when newOptionPosition is 'last')
    const processedOptions = React.useMemo(() => {
        if (!isNewOption && !options) return options;

        // Start with the auto-created "Add New" option if isNewOption is true
        const newOptions: any[] = [];

        if (addNewOption) {
            newOptions.push(addNewOption);
        }

        if (!options) {
            return newOptions.length > 0 ? newOptions : options;
        }

        // Handle both array and grouped options
        if (Array.isArray(options)) {
            const regularOptions: any[] = [];
            const groups: any[] = [];

            options.forEach((option: any) => {
                // Check if it's a group (has 'options' property)
                if (option.options && Array.isArray(option.options)) {
                    // Extract new options from groups
                    const groupNewOptions: any[] = [];
                    const groupRegularOptions: any[] = [];

                    option.options.forEach((groupOption: any) => {
                        if (groupOption.isNewOption === true) {
                            groupNewOptions.push(groupOption);
                        } else {
                            groupRegularOptions.push(groupOption);
                        }
                    });

                    // Add new options from group to top-level new options
                    newOptions.push(...groupNewOptions);

                    // Rebuild group with only regular options
                    if (groupRegularOptions.length > 0) {
                        groups.push({
                            ...option,
                            options: groupRegularOptions,
                        });
                    }
                } else if (option.isNewOption === true) {
                    // Top-level new option (from user-provided options)
                    newOptions.push(option);
                } else {
                    // Top-level regular option
                    regularOptions.push(option);
                }
            });

            // Return new options first (default) or last, depending on newOptionPosition
            return newOptionPosition === 'last' ? [...groups, ...regularOptions, ...newOptions] : [...newOptions, ...groups, ...regularOptions];
        }

        // If options is not an array, return it with addNewOption prepended/appended if needed
        if (!addNewOption) return options;

        return newOptionPosition === 'last' ? [options, addNewOption] : [addNewOption, options];
    }, [options, isNewOption, addNewOption, newOptionPosition]);

    // Custom filter function that always includes new options
    const customFilterOption = React.useCallback(
        (option: any, searchText: string) => {
            // Always show "Add New" option when isNewOption is enabled
            if (isNewOption && checkIsAddNewOption(option)) {
                return true;
            }

            // Always show options marked as address (assume they are pre-filtered/fetched based on input)
            if (option.data?.isAddress || option.isAddress) {
                return true;
            }

            // For all other options, apply standard filtering
            const optionData = option.data || option;
            const optionLabel = option.label || optionData.label || '';
            const searchValue = searchText ? searchText.toLowerCase().trim() : '';

            // If search is empty, show all options
            if (!searchValue) {
                return true;
            }

            // Filter based on search text
            return optionLabel.toLowerCase().includes(searchValue);
        },
        [isNewOption, checkIsAddNewOption]
    );

    // Manual outside click detection to fix react-select's broken event listeners (only once)
    useEffect(() => {
        if (menuIsOpenList && !hasUsedManualFix) {
            const handleDocumentClick = (event: MouseEvent) => {
                const selectContainer = selectRef.current?.controlRef?.closest('.wal-hq-select');
                const menuPortal = document.querySelector('.select__menu-portal');

                const isOutsideSelect = selectContainer && !selectContainer.contains(event.target as Node);
                const isOutsideMenu = menuPortal && !menuPortal.contains(event.target as Node);

                if (isOutsideSelect && isOutsideMenu) {
                    setMenuIsOpenList(false);
                    setHasUsedManualFix(true);
                }
            };

            const timer = setTimeout(() => {
                document.addEventListener('click', handleDocumentClick, true);
            }, 50);

            return () => {
                clearTimeout(timer);
                document.removeEventListener('click', handleDocumentClick, true);
            };
        }
    }, [menuIsOpenList, hasUsedManualFix]);

    useEffect(() => {
        if (selectRef?.current) {
            if (isFocused) {
                selectRef.current.focus();
            } else if (!isFocused) {
                selectRef.current.blur();
            }
        }
    }, [isFocused]);

    const checkOverflow = (isRecall = false) => {
        const valueContainer = document?.querySelector('.select__value-container--is-multi.select__value-container--has-value');

        if (valueContainer) {
            const hasHorizontalOverflow = valueContainer.scrollWidth > valueContainer.clientWidth;

            if (hasHorizontalOverflow) {
                if (maxVisibleValues - 1 !== maxVisibleValue) {
                    setMaxVisibleValue(maxVisibleValues - 1);
                }
            } else {
                if (!isRecall && maxVisibleValues === value?.length) {
                    setMaxVisibleValue(maxVisibleValues);
                }
            }

            return valueContainer;
        }

        return null;
    };

    useEffect(() => {
        // Check overflow on mount and when value changes
        const valueContainer = checkOverflow();

        // Add resize observer to check overflow when container size changes
        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });

        if (valueContainer) {
            resizeObserver.observe(valueContainer);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [value]);

    useEffect(() => {
        checkOverflow(true);
    }, [maxVisibleValue]);

    useEffect(() => {
        if (inputValue && forceSearchable) {
            checkOverflow(true);
        }
    }, [inputValue]);

    const CustomOption = ({ ...props }: any) => {
        const { ...rest } = props.innerProps;

        // Check if this is the "Add New" option
        const isAddNewOptionFlag = checkIsAddNewOption(props.data || props);

        const isCreateNewOption = props.data?.__isNew__ === true;

        // Prevent click events when option is loading
        const modifiedInnerProps = isOptionLoading
            ? {
                  ...rest,
                  onClick: (e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                  },
                  onMouseDown: (e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                  },
              }
            : {
                  ...rest,
                  onClick: (e: React.MouseEvent) => {
                      setInputValue('');

                      if (rest.onClick) {
                          rest.onClick(e);
                      }
                  },
              };

        const newProps = { ...props, innerProps: modifiedInnerProps };

        return (
            <components.Option
                {...newProps}
                className={
                    isAddNewOptionFlag
                        ? `${props.className || ''} select-option-add-new bg-primary bg-opacity-[0.05] hover:bg-primary hover:bg-opacity-10 ${
                              props.isSelected || props.isFocused ? '!border-0 focus:!border-0' : ''
                          }`
                        : props.data.isDeleted
                        ? `${props.className || ''} !opacity-50`
                        : props.className
                }
            >
                {isOptionLoading ? (
                    <>
                        <div className="flex justify-between items-center align-middle">
                            <div className={`flex justify-center items-center w-full flex-1 max-w-[calc(100%_-_15px)] ${selectValueWrapClass}`}>
                                {!isNotShowImage && (
                                    <div
                                        className={`w-5 h-5 leading-[20px] mr-2 text-[8px] rounded-full font-bold flex items-center justify-center uppercase flex-none custom-loading image-loading ${
                                            showCustomerImage ? 'table-profile-customer-circle-xl' : 'bg-primary text-white'
                                        }`}
                                    >
                                        <span>{'WA'}</span>
                                    </div>
                                )}

                                <div className={`flex-1 flex flex-wrap truncate`}>
                                    <span className="flex justify-center items-center font-medium text-grayLight900 underline flex-1 custom-loading">
                                        ABF FREIGHT SYSTEM INC ABF FREIGHT SYSTEM INC
                                    </span>
                                </div>
                            </div>
                        </div>
                    </>
                ) : isShowLoadingSearchText ? (
                    <>
                        <div className="flex justify-between items-center">
                            <p className="text-grayLight900 text-xs font-normal">{loadingSearchText}</p>

                            <p className={`loading loading-spinner text-primary w-[18px] h-[18px] }`}></p>
                        </div>
                    </>
                ) : (
                    <div className="flex justify-between items-center align-middle">
                        <div className={`w-full flex gap-x-1.5 ${selectLabelWrapClass}  ${!isMultipleSelection && 'max-w-[calc(100%_-_15px)]'}`}>
                            {isMultipleSelection && !isDisableCheckbox && !isCreateNewOption && (
                                <>
                                    <CheckBox
                                        parentClassName="mt-0.5"
                                        classes="z-10"
                                        onChangeFunc={() => {}}
                                        // `option.isChecked` lets a caller show an option as ticked without it being part of
                                        // `value` (e.g. recipients already saved on an order). Disabled options render a
                                        // disabled checkbox so they don't look clickable.
                                        checked={
                                            props.data?.isChecked === true ||
                                            (isMultipleEquipmentType ? Array.isArray(value) && value.some((item) => item.value === props.data.value) : props.isSelected)
                                        }
                                        disabled={props.isDisabled}
                                    />
                                </>
                            )}

                            <div className={`flex justify-center items-center w-full flex-1 truncate  ${selectValueWrapClass}`}>
                                {props.data.icon ? (
                                    <span className="mr-2">{props.data.icon}</span>
                                ) : props.data.image ? (
                                    <img
                                        alt=""
                                        className={`rounded-full border border-utilityBlack border-opacity-[0.08] flex-none ${
                                            size === 'xs' ? 'select-dropdown-icon-xs' : 'w-5 h-5 mr-2'
                                        } ${dropdownImageClassName}`}
                                        src={props.data.image}
                                        onError={onError}
                                    />
                                ) : (
                                    props.data.isImage && (
                                        <div
                                            className={`text-[8px] leading-[20px] rounded-full font-bold flex items-center justify-center uppercase flex-none ${
                                                size === 'xs' ? 'select-dropdown-icon-xs' : 'w-5 h-5 mr-2 '
                                            } ${showCustomerImage ? 'bg-utilityGray100 text-primary' : 'bg-primary text-white'}`}
                                        >
                                            <span>{getShortName(props.data.label)}</span>
                                        </div>
                                    )
                                )}
                                {props.data.value === 'all_user' ? <div className="w-5 h-5 mr-2"></div> : ''}
                                <div className={`flex-1 flex   truncate ${selectoptionWraptext} ${truncateToOneLine ? '' : 'flex-wrap'} `}>
                                    <span
                                        className={`truncate font-medium mr-2 empty:hidden ${selectoptiontext} ${isAddNewOptionFlag ? 'text-primary font-semibold' : ''} ${
                                            truncateToOneLine && props.data.subLabel ? 'flex-none max-w-[50%]' : ''
                                        }`}
                                    >
                                        {props.data.label}
                                    </span>

                                    {props.data.subLabel ? (
                                        <span
                                            className={`font-normal sub-label mr-1.5 ${selectOptionSubLabelClass} ${truncateToOneLine ? 'truncate' : 'flex justify-center items-center '}`}
                                            dangerouslySetInnerHTML={{
                                                __html: props.data.subLabel,
                                            }}
                                        ></span>
                                    ) : (
                                        ''
                                    )}

                                    {props.data.isVerified && <img src={verifiedIcon} className="w-3.5 h-3.5 mt-0.5" alt="verified" />}
                                    {props.data.email ? <span className="flex justify-center items-center font-medium text-grayLight900 underline">{props.data.email}</span> : ''}
                                </div>
                            </div>
                        </div>
                        {props.isSelected ? <Check className={`w-3 h-3 text-primary flex-none select-checkmark`} /> : ''}{' '}
                    </div>
                )}
            </components.Option>
        );
    };

    const CustomNoOptionsMessage = (props: NoticeProps<any>) => {
        if (hideNoOptionsMessage) {
            return null;
        }

        return (
            <components.NoOptionsMessage {...props}>
                <p className="text-[11px] text-textSecondary font-bold -my-[1px]">{noOptionMessage}</p> {/* Customize this message as needed */}
            </components.NoOptionsMessage>
        );
    };

    // Custom clear indicator to prevent dropdown from opening when clearing
    const CustomClearIndicator = (props: any) => {
        const { innerProps } = props;

        return (
            <components.ClearIndicator
                {...props}
                innerProps={{
                    ...innerProps,
                    onMouseDown: (e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        props.clearValue();

                        if (onClearFunc) {
                            setInputValue('');
                            onClearFunc();
                        }
                    },
                }}
            />
        );
    };

    useEffect(() => {
        setMenuIsOpenList(menuIsOpen && isMenuOpenManually);
    }, [menuIsOpen]);

    useEffect(() => {
        if (isCloseOnScroll && menuIsOpenList) {
            const handleScroll = () => {
                // A scroll the SELECTION itself caused (the control being refocused, the content
                // below the control growing) must not close the menu keepMenuOpenOnMultiSelect
                // promises to keep open. Only a scroll the user performs should close it, and by
                // then the commit has settled and this flag is back to false.
                if (isCommittingSelectionRef.current) {
                    return;
                }

                setMenuIsOpenList(false);
                setIsFocused(false);
            };

            const scrollableContainer = document.querySelector('.main__layout_section');

            const scrollableModalContainer = document.querySelector('.close-on-scroll');

            if (scrollableContainer) {
                scrollableContainer.addEventListener('scroll', handleScroll, true);
            }

            if (scrollableModalContainer) {
                scrollableModalContainer.addEventListener('scroll', handleScroll, true);
            }

            return () => {
                if (scrollableContainer) {
                    scrollableContainer.removeEventListener('scroll', handleScroll, true);
                }

                if (scrollableModalContainer) {
                    scrollableModalContainer.removeEventListener('scroll', handleScroll, true);
                }
            };
        }
    }, [isCloseOnScroll, menuIsOpenList]);

    useEffect(() => {
        if (menuIsOpenList && selectRef.current) {
            selectRef.current.focus();
        }
    }, [menuIsOpenList]);

    useEffect(() => {
        if (!isMultipleSelection && selectRef.current && selectRef.current.state.focusedOption && selectRef.current?.state.focusedOption?.value !== value?.value) {
            selectRef.current.state.focusedOption = '';
        }
    }, [menuIsOpen]);

    const calculatePlacement = () => {
        if (menuPlaceOption === 'auto' && selectRef.current) {
            const control = selectRef.current.controlRef;

            if (control) {
                const rect = control.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;

                const optionsCount = Array.isArray(processedOptions)
                    ? processedOptions.some((option) => option?.options)
                        ? processedOptions?.flatMap((option) => option?.options)?.length
                        : processedOptions?.length
                    : 5;
                const requiredSpace = Math.min(optionsCount * 36 + 20, 200);

                if (spaceBelow < requiredSpace) {
                    setPlacement('top');
                } else {
                    setPlacement('bottom');
                }
            }
        } else {
            setPlacement(menuPlaceOption);
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
        setMenuIsOpenList(true && isMenuOpenManually);
    };

    useEffect(() => {
        if (menuIsOpenList && selectRef.current) {
            selectRef?.current?.setState({
                focusedOption: null,
            });

            // Focus on the first option when the menu opens
            if (selectRef.current?.select?.menuListRef) {
                const firstOption = selectRef.current.select.menuListRef?.querySelector('div:first-child');

                if (firstOption) {
                    firstOption.blur();
                }
            }
        }
    }, [menuIsOpenList]);

    const handleBlur = () => {
        // Momentary blur from committing a pick — focus is restored on the next tick, so dropping
        // isFocused here only makes the control flash its placeholder back in mid-selection.
        if (isCommittingSelectionRef.current) {
            return;
        }

        setIsFocused(false);
        setInputValue('');
    };

    const handleKeyDown = (event: React.KeyboardEvent, isCreatableSelect = false) => {
        if (customOnKeyDown) {
            customOnKeyDown(event);

            if (event.defaultPrevented) {
                return;
            }
        }

        if (event.key === 'Tab' && !menuIsOpenList && !isPreventTabPress) {
            setMenuIsOpenList(true && isMenuOpenManually);

            if (selectRef.current) {
                selectRef.current.focus();
            }
        }

        if (event.key === 'Tab' && isPreventTabPress) {
            event.preventDefault();
            event.stopPropagation();
        }

        // Swallow Space when it cannot mean "type a space": on a non-searchable select, and — for
        // isPreventKeyboardSelect — while the menu is open with an empty search box, which is exactly
        // when react-select reads Space as "select the focused option" and ticks a checkbox nobody
        // clicked. Once there is typed text, Space is a real character and must pass through.
        if (event.key === ' ' && (!isSearchable || (isPreventKeyboardSelect && menuIsOpenList && !inputValue))) {
            event.preventDefault();
        }

        // Prevent dropdown from closing when backspace is pressed on empty input
        if (event.key === 'Backspace' && inputValue === '' && isSearchable && (!isCreatableSelect || menuIsOpenList)) {
            event.preventDefault();
            event.stopPropagation();

            if (isCreatableSelect && selectRef.current) {
                setTimeout(() => {
                    selectRef.current.focus();
                    setMenuIsOpenList(true && isMenuOpenManually);
                    setIsFocused(true);
                }, 0);
            }
        }
    };

    const handleMenuOpen = () => {
        calculatePlacement();
        setIsFocused(true);
        dispatch(setTooltipGlobalOpen(false));
        setMenuIsOpenList(true && isMenuOpenManually);

        if (onMenuOpen) {
            onMenuOpen();
        }
    };

    const handleMenuClose = () => {
        // react-select fires this off the blur a pick causes. Closing here unmounts the whole menu
        // and the refocus above rebuilds it from scratch, so the dropdown visibly flickers on every
        // pick. keepMenuOpenOnMultiSelect promises the menu stays open — honour it literally instead
        // of closing and reopening.
        if (isCommittingSelectionRef.current) {
            return;
        }

        if (isCloseOnScroll) {
            setMenuIsOpenList(false);
            setIsFocused(false);
        }

        if (!isShowInputValue) {
            setInputValue('');
        }

        if (onMenuClose) {
            onMenuClose();
        }
    };

    const FormatOptionLabel = ({ option, isCreatableSelect = false }: { option: any; isCreatableSelect?: boolean }) => (
        <div className={`flex justify-between ${selectValueMainClass}`}>
            <div className={`flex items-center w-full ${selectValueClass} `}>
                {option.customLabel ? <span className="font-medium ">{option.customLabel}</span> : ''}
                {option?.icon ? (
                    <span className="mr-2 empty:hidden">{option.icon}</span>
                ) : option?.image ? (
                    <img
                        alt=""
                        className={`rounded-full border border-utilityBlack border-opacity-[0.08] ${size === 'xs' ? 'select-icon-xs' : 'w-[18px] h-[18px] mr-1.5 '} ${imageClassName}`}
                        src={option.image}
                        onError={onError}
                    />
                ) : (
                    option.isImage && (
                        <div
                            className={`leading-[18px]  text-[8px] rounded-full font-bold flex items-center justify-center uppercase flex-none ${
                                size === 'xs' ? 'select-icon-xs' : 'w-[18px] h-[18px] mr-1.5 '
                            } ${showCustomerImage ? 'table-profile-customer-circle-xl' : 'bg-primary text-white'}`}
                        >
                            <span>{getShortName(option.label)}</span>
                        </div>
                    )
                )}
                <span className="truncate font-medium">
                    {option.datetime && (
                        <ActionTooltip message={option.datetime} openOnHover={true} isOpenChildTopPosOnTop={true} tooltipPosition="center" parentClassName="w-max" type="light">
                            {option.label}
                        </ActionTooltip>
                    )}
                    {!option.datetime && !option.customLabel ? option.label : ''}
                </span>

                {secondaryLabel && (
                    <>
                        <span className={isCreatableSelect ? 'mx-2 text-borderPrimary' : 'mx-1 text-grayLight500'}>|</span>
                        {secondaryLabel?.image ? (
                            <img
                                src={secondaryLabel?.image}
                                alt={secondaryLabel?.label}
                                className={`rounded-full w-[18px] h-[18px] border border-utilityBlack border-opacity-[0.08] flex-none ${isCreatableSelect ? 'mr-1.5' : 'mr-1'}`}
                            />
                        ) : (
                            <div
                                className={`w-[18px] h-[18px] leading-[18px] text-[8px] mr-1.5 rounded-full font-bold flex items-center justify-center uppercase flex-none ${
                                    isCreatableSelect ? 'bg-utilityGray100 text-primary' : 'table-profile-customer-circle-xl'
                                }`}
                            >
                                <span>{getShortName(secondaryLabel?.label)}</span>
                            </div>
                        )}
                        <span className={`font-medium ${isCreatableSelect ? 'truncate' : 'text-grayLight900'}`}>{secondaryLabel?.label}</span>
                    </>
                )}

                {option.email ? <span className="flex justify-center items-center font-medium underline ml-2">{option.email}</span> : ''}
                {option.subLabel ? (
                    isCreatableSelect ? (
                        <span
                            className={`flex justify-center items-center font-medium ml-2 ${selectInputSubLabelClass}`}
                            dangerouslySetInnerHTML={{
                                __html: option.subLabel,
                            }}
                        />
                    ) : (
                        <span className={`flex justify-center items-center font-medium ml-2 ${selectInputSubLabelClass}`}>{option.subLabel}</span>
                    )
                ) : (
                    ''
                )}

                {option.isVerified && <img src={verifiedIcon} className="w-3.5 h-3.5 ml-1.5" alt="verified" />}
            </div>
        </div>
    );

    const handleChange = (newValue: any, actionMeta: any) => {
        // Handle "Add New" option selection
        if (isNewOption && actionMeta.action === 'select-option' && (checkIsAddNewOption(newValue) || (Array.isArray(newValue) && newValue.some((item) => checkIsAddNewOption(item))))) {
            // Call handleCreate if provided
            if (handleCreate) {
                handleCreate();
            }

            // Don't call onChangeFunc and don't set the value
            // Reset to prevent selecting the "Add New" option
            return;
        }

        // Keep menu open and focused for multi-select with keepMenuOpenOnMultiSelect
        if (keepMenuOpenOnMultiSelect && isMultipleSelection && actionMeta.action !== 'clear') {
            isCommittingSelectionRef.current = true;
            setIsFocused(true);
        } else {
            setIsFocused(false);
        }

        if (isOptionLoading && actionMeta.action === 'select-option') {
            return;
        }

        if (newValue === null && actionMeta.action === 'clear') {
            if (onChangeFunc) onChangeFunc(null, actionMeta);

            if (onClearFunc) {
                setInputValue('');
                onClearFunc();
            }
            setMenuIsOpenList(false);

            if (!maintainFocusOnClear) {
                setTimeout(() => {
                    if (selectRef.current) {
                        selectRef.current.blur();
                    }
                }, 10);
            } else {
                setTimeout(() => {
                    if (selectRef.current) {
                        selectRef.current.focus();
                        setMenuIsOpenList(true && isMenuOpenManually);
                        setIsFocused(true);
                    }
                }, 100);
            }
        } else {
            // Handle pagination optimization for records per page
            if (pagination?.isPagination && newValue?.value !== undefined) {
                const { totalRecords, currentLimit, skipApiCallRef } = pagination;

                if ((totalRecords <= currentLimit && totalRecords <= newValue.value) || totalRecords == 0) {
                    skipApiCallRef.current = true;
                }
            }

            if (onChangeFunc) onChangeFunc(newValue, actionMeta); // Normal change handling

            if (!isMultipleEquipmentType && isMultipleSelection && newValue?.length === 0 && actionMeta.action === 'deselect-option' && !keepMenuOpenOnMultiSelect) {
                if (selectRef.current) {
                    selectRef.current.blur();
                }
            }

            // Keep focus and menu open after selection when keepMenuOpenOnMultiSelect is true
            if (keepMenuOpenOnMultiSelect && isMultipleSelection && actionMeta.action !== 'clear') {
                setTimeout(() => {
                    if (selectRef.current) {
                        // preventScroll: react-select's own focus() lets the browser scroll the control
                        // back into view, and ANY scroll of the layout section closes this menu (see
                        // handleScroll). Refocusing without it closed the very menu we reopen here.
                        const inputEl = selectRef.current?.select?.inputRef ?? selectRef.current?.inputRef;

                        if (inputEl?.focus) {
                            inputEl.focus({ preventScroll: true });
                        } else {
                            selectRef.current.focus();
                        }
                        setMenuIsOpenList(true && isMenuOpenManually);
                    }
                    isCommittingSelectionRef.current = false;
                }, 0);
            }
        }

        if ((actionMeta.action === 'remove-value' || actionMeta.action === 'clear') && (newValue?.value === null || newValue?.length === 0)) {
            if (newValue?.value === null || newValue?.length === 0) {
                setIsFocused(true);

                if (!maintainFocusOnClear) {
                    setTimeout(() => {
                        if (selectRef.current) {
                            selectRef.current.blur();
                        }
                    }, 400);
                } else {
                    setTimeout(() => {
                        if (selectRef.current) {
                            selectRef.current.focus();
                            setMenuIsOpenList(true && isMenuOpenManually);
                        }
                    }, 100);
                }
            }
        }
    };

    const handleInputChange = (newValue: string, actionMeta: any) => {
        if (maxInputSearchLength && actionMeta.action === 'input-change') {
            const limitedValue = newValue.slice(0, maxInputSearchLength);
            setInputValue(limitedValue);

            if (onInputChange) {
                onInputChange(limitedValue, actionMeta);
            }

            return limitedValue;
        } else {
            if (!isShowInputValue) {
                setInputValue(newValue);
            }

            if (onInputChange) {
                onInputChange(newValue, actionMeta);
            }
        }
    };

    const CustomMultiValueContainer = (props: any) => {
        const { selectProps, data } = props;
        const selectedValues = selectProps.value;

        if (isMultipleEquipmentType && selectedValues?.length > maxVisibleValue) {
            if (selectedValues.indexOf(data) < maxVisibleValue) {
                return <components.MultiValueContainer {...props} />;
            } else if (selectedValues.indexOf(data) === selectedValues.length - 1) {
                return (
                    <div className="inline select__multi-value text-right flex-none">
                        <span className="pl-1 pr-2">...</span>
                        <div className="select__multi-value__label">{`+${selectedValues.length - maxVisibleValue}`}</div>
                    </div>
                );
            }

            return null;
        }

        return <components.MultiValueContainer {...props} />;
    };

    /**
     * ── isStableOptionRender (opt-in; everything here is inert when false) ─────────────────────
     *
     * CustomOption and friends are declared in this render body, and the `components={{ … }}`
     * literals below are inline, so each one is a NEW function on every render. react-select 5.8
     * does not memoize any of this — getComponents() just calls defaultComponents(this.props) each
     * time — so React sees a new component TYPE for `Option` and UNMOUNTS AND REMOUNTS every row of
     * the open menu on each render. A single click re-renders this component several times
     * (setInputValue, setIsFocused, plus the parent's own state), so the whole menu is torn down
     * and rebuilt several times per pick. That is the flicker.
     *
     * The proxies below keep a stable identity (empty dep arrays) and call the CURRENT
     * implementation through a ref, so closed-over props and state stay live while React updates
     * the existing rows in place instead of replacing them.
     *
     * Only the component VALUES need to be stable — the surrounding object may keep being rebuilt,
     * since React compares element types, not the props object that carried them.
     *
     * Safe because none of the proxied components use hooks: they are invoked as plain functions,
     * so any hook inside them would bind to the proxy. If one ever needs a hook, hoist it to module
     * scope (as TruncatedSingleValue already is) rather than adding it here.
     */
    const liveRef = useRef({ CustomOption, CustomNoOptionsMessage, CustomMultiValueContainer, CustomClearIndicator, isFocused, isSearchable, placeholderIcon });
    liveRef.current = { CustomOption, CustomNoOptionsMessage, CustomMultiValueContainer, CustomClearIndicator, isFocused, isSearchable, placeholderIcon };

    const StableOption = React.useMemo(() => (props: any) => liveRef.current.CustomOption(props), []);
    const StableNoOptionsMessage = React.useMemo(() => (props: any) => liveRef.current.CustomNoOptionsMessage(props), []);
    const StableMultiValueContainer = React.useMemo(() => (props: any) => liveRef.current.CustomMultiValueContainer(props), []);
    const StableClearIndicator = React.useMemo(() => (props: any) => liveRef.current.CustomClearIndicator(props), []);
    const StablePlaceholder = React.useMemo(
        () => (props: any) => {
            const { isFocused: focused, isSearchable: searchable, placeholderIcon: currentIcon } = liveRef.current;

            return (
                <components.Placeholder {...props}>
                    {focused && searchable ? false : currentIcon}
                    {focused && searchable ? false : props.children}
                </components.Placeholder>
            );
        },
        []
    );

    return (
        <>
            <div className={`${parentClassName} ${size === 'sm' ? 'wal-hq-select-v2' : size === 'xs' ? 'select-with-dropdown-xs' : ''} ${isShowAiStar ? 'ai-gradient-border-wrap' : ''}`}>
                <div className={`flex justify-between gap-x-2 ${labelWrapClassName}`}>
                    {label && (
                        <div className="flex">
                            <label className={labelClassName}>{label}</label>
                            {required && <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>}
                        </div>
                    )}
                    {labelRight && <>{labelRight}</>}
                </div>
                <div className={`wal-hq-select relative  ${isLoading ? 'custom-loading rounded-lg' : ''} ${errorText || isError ? 'is-invalid' : ''} ${selectWrapClass}`}>
                    {autoCompleteSelect ? (
                        <CreatableSelect
                            ref={selectRef}
                            value={value}
                            inputValue={isMultipleSelection ? inputValue : !value || (Array.isArray(value) && value.length === 0) ? inputValue : ''}
                            defaultValue={defaultValue}
                            onChange={handleChange}
                            onInputChange={handleInputChange}
                            onCreateOption={onCreateOption}
                            isValidNewOption={(inputVal) =>
                                // if inputVal is empty or only whitespace, return false
                                formatCreateLabelDefaultShowText ? true : Boolean(inputVal?.trim()) && isValidNewOption
                            }
                            formatCreateLabel={(inputVal) => {
                                if (isOptionLoading || isShowLoadingSearchText) {
                                    return null;
                                }

                                // Display-only: the caller may re-render what was typed (a formatted phone,
                                // say). onCreateOption below still gets react-select's raw inputVal.
                                const createLabelValue = formatCreateLabelValue ? formatCreateLabelValue(inputVal) : inputVal;

                                if (formatCreateLabelText || formatCreateLabelDefaultShowText) {
                                    let displayText: string | undefined = undefined;

                                    if ((!inputVal || inputVal.trim() === '') && formatCreateLabelDefaultShowText) {
                                        displayText = formatCreateLabelDefaultShowText;
                                    } else {
                                        displayText = formatCreateLabelText?.includes(COMMON_CONSTANTS.DYNAMIC_CREATE_LABEL_TEXT)
                                            ? formatCreateLabelText?.replace(COMMON_CONSTANTS.DYNAMIC_CREATE_LABEL_TEXT, createLabelValue)
                                            : formatCreateLabelDefaultShowText
                                            ? formatCreateLabelDefaultShowText
                                            : `${formatCreateLabelText} ${createLabelValue}`;
                                    }

                                    return displayText ? (
                                        <span className="flex items-center gap-1.5">
                                            {formatCreateLabelIcon}
                                            <span className={formatCreateLabelTextClass || ''}>{displayText}</span>
                                        </span>
                                    ) : null;
                                }

                                if (createOptionText) {
                                    return `${createOptionText} "${createLabelValue}"`;
                                }

                                return `${t('Common.Create')} "${createLabelValue}"`;
                            }}
                            createOptionPosition={createOptionPosition}
                            options={processedOptions}
                            menuPlacement={placement}
                            formatOptionLabel={(option: any) => <FormatOptionLabel option={option} isCreatableSelect={false} />}
                            hideSelectedOptions={false}
                            tabIndex={tabIndex}
                            classNamePrefix={classNamePrefix}
                            isSearchable={forceSearchable ? true : value?.length || Boolean(value && value.value) ? false : isSearchable}
                            onKeyDown={(event) => handleKeyDown(event, true)}
                            // false → Tab just moves focus out instead of committing the focused option
                            tabSelectsValue={!isPreventKeyboardSelect}
                            filterOption={isNewOption ? customFilterOption : onInputChange ? null : undefined}
                            isClearable={isClearable}
                            isDisabled={isDisabled}
                            isMulti={isMultipleSelection}
                            placeholder={placeholder}
                            className={`${classComp}`}
                            components={{
                                ...(isDisabled && isMultipleSelection ? { MultiValueRemove: () => null } : {}),
                                ...(isHideDropdownIndicator
                                    ? {
                                          DropdownIndicator: () => null,
                                          IndicatorSeparator: () => null,
                                      }
                                    : {}),
                                Option: isStableOptionRender ? StableOption : CustomOption,
                                NoOptionsMessage: isStableOptionRender ? StableNoOptionsMessage : CustomNoOptionsMessage,
                                MultiValueContainer: isStableOptionRender ? StableMultiValueContainer : CustomMultiValueContainer,
                                ...(isShowTooltipOnTruncate && !isMultipleSelection ? { SingleValue: TruncatedSingleValue } : {}),
                                ...(isHideCheckbox && { MultiValueRemove: () => null }),
                                ClearIndicator: isClearable ? (isStableOptionRender ? StableClearIndicator : CustomClearIndicator) : undefined,
                                Placeholder: isStableOptionRender
                                    ? StablePlaceholder
                                    : (props: any) => (
                                          <components.Placeholder {...props}>
                                              {isFocused && isSearchable ? false : placeholderIcon}
                                              {isFocused && isSearchable ? false : props.children}
                                          </components.Placeholder>
                                      ),
                            }}
                            closeMenuOnSelect={closeMenuOnSelect}
                            menuIsOpen={menuIsOpenList}
                            menuPortalTarget={inlineSelect ? null : document.body}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 1111 }),
                                menu: (base) => {
                                    // For CreatableSelect, show menu if create option should be visible
                                    // trim the input value for the create option
                                    const hasCreateOption = (inputValue?.trim() && isValidNewOption) || formatCreateLabelDefaultShowText;
                                    const shouldHideMenu = hideNoOptionsMessage && (!processedOptions || processedOptions.length === 0) && !hasCreateOption;

                                    return {
                                        ...base,
                                        display: shouldHideMenu ? 'none' : 'block',
                                    };
                                },
                                option: (base, state) => {
                                    if (checkIsAddNewOption(state.data) && (state.isSelected || state.isFocused)) {
                                        return {
                                            ...base,
                                            border: 'none',
                                            borderLeft: 'none',
                                            borderRight: 'none',
                                            borderTop: 'none',
                                            borderBottom: 'none',
                                            boxShadow: 'none',
                                            outline: 'none',
                                        };
                                    }

                                    return base;
                                },
                            }}
                            classNames={{
                                menuPortal: () => `wal-hq-select ${isOptionLoading ? 'select-dropdown-loading' : ''} ${size === 'xs' && 'select-dropdown-xs'} ${SelectDropdownWrapClass}`,
                                option: (state) => {
                                    const isAddNew = checkIsAddNewOption(state.data);
                                    const isCreateNew = state.data.__isNew__ && isHideHoverWithCreateOption;

                                    if (isAddNew) {
                                        return `select-option-add-new bg-primary bg-opacity-[0.05] hover:bg-primary hover:bg-opacity-10 ${
                                            state.isSelected || state.isFocused ? '!border-0 focus:!border-0' : ''
                                        }`;
                                    }

                                    if (isCreateNew) {
                                        return 'select-dropdown-label';
                                    }

                                    return '';
                                },
                            }}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onMenuOpen={handleMenuOpen}
                            onMenuClose={handleMenuClose}
                        />
                    ) : (
                        <ReactSelect
                            ref={selectRef}
                            value={value}
                            inputValue={isMultipleSelection ? inputValue : !value || (Array.isArray(value) && value?.length === 0) ? inputValue : ''}
                            defaultValue={defaultValue}
                            onChange={handleChange}
                            onInputChange={handleInputChange}
                            options={processedOptions}
                            menuPlacement={placement}
                            formatOptionLabel={(option: any) => <FormatOptionLabel option={option} isCreatableSelect={true} />}
                            hideSelectedOptions={false}
                            tabIndex={tabIndex}
                            classNamePrefix={classNamePrefix}
                            isSearchable={forceSearchable ? true : value?.length || Boolean(value && value.value) ? false : isSearchable}
                            onKeyDown={(event) => handleKeyDown(event, false)}
                            // false → Tab just moves focus out instead of committing the focused option
                            tabSelectsValue={!isPreventKeyboardSelect}
                            filterOption={isNewOption ? customFilterOption : onInputChange ? null : undefined}
                            isClearable={isClearable}
                            isDisabled={isReadOnly || isDisabled}
                            isMulti={isMultipleSelection}
                            placeholder={placeholder}
                            className={`${classComp}`}
                            components={{
                                ...(isDisabled && isMultipleSelection ? { MultiValueRemove: () => null } : {}),
                                ...(isHideDropdownIndicator
                                    ? {
                                          DropdownIndicator: () => null,
                                          IndicatorSeparator: () => null,
                                      }
                                    : {}),
                                Option: isStableOptionRender ? StableOption : CustomOption,
                                NoOptionsMessage: isStableOptionRender ? StableNoOptionsMessage : CustomNoOptionsMessage,
                                MultiValueContainer: isStableOptionRender ? StableMultiValueContainer : CustomMultiValueContainer,
                                ...(isShowTooltipOnTruncate && !isMultipleSelection ? { SingleValue: TruncatedSingleValue } : {}),
                                ...(!isDisableCheckbox && { MultiValueRemove: () => null }),
                                ClearIndicator: isClearable ? (isStableOptionRender ? StableClearIndicator : CustomClearIndicator) : undefined,
                                Placeholder: isStableOptionRender
                                    ? StablePlaceholder
                                    : (props: any) => (
                                          <components.Placeholder {...props}>
                                              {isFocused && isSearchable ? false : placeholderIcon}
                                              {isFocused && isSearchable ? false : props.children}
                                          </components.Placeholder>
                                      ),
                            }}
                            closeMenuOnSelect={closeMenuOnSelect ?? (isMultipleEquipmentType ? false : true)}
                            menuIsOpen={menuIsOpenList}
                            menuPortalTarget={inlineSelect ? null : document.body}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9000 }),
                                menu: (base) => ({
                                    ...base,
                                    display: hideNoOptionsMessage && (!processedOptions || processedOptions.length === 0) ? 'none' : 'block',
                                }),
                                option: (base, state) => {
                                    if (checkIsAddNewOption(state.data) && (state.isSelected || state.isFocused)) {
                                        return {
                                            ...base,
                                            border: 'none',
                                            borderLeft: 'none',
                                            borderRight: 'none',
                                            borderTop: 'none',
                                            borderBottom: 'none',
                                            boxShadow: 'none',
                                            outline: 'none',
                                        };
                                    }

                                    return base;
                                },
                            }}
                            classNames={{
                                menuPortal: () => `wal-hq-select ${isOptionLoading ? 'select-dropdown-loading' : ''} ${size === 'xs' && 'select-dropdown-xs'} ${SelectDropdownWrapClass}`,
                                option: (state) => {
                                    const isAddNew = checkIsAddNewOption(state.data);

                                    if (isAddNew) {
                                        return `select-option-add-new bg-primary bg-opacity-[0.05] hover:bg-primary hover:bg-opacity-10 ${
                                            state.isSelected || state.isFocused ? '!border-0 focus:!border-0' : ''
                                        }`;
                                    }

                                    return '';
                                },
                            }}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            onMenuOpen={handleMenuOpen}
                            menuPosition={menuPosition}
                            onMenuClose={handleMenuClose}
                        />
                    )}
                    {icon && icon}
                    {isShowAiStar && (
                        <div className="absolute top-1/2 -translate-y-1/2 left-2 w-4 h-4 mt-px">
                            <ActionTooltip message={'Detected with AI'} isOpenChildTopPosOnTop={true} tooltipPosition="center" parentClassName="min-w-[120px]" openOnHover={true} type="light">
                                <Star06 className="w-4 h-4 text-primary " />
                            </ActionTooltip>
                        </div>
                    )}
                </div>
                {isShowErrorMsg && errorText && typeof errorText !== 'boolean' && (
                    <div className="[&:empty]:mt-0 mt-1.5">
                        <ErrorMsg errorText={errorText}></ErrorMsg>
                    </div>
                )}
            </div>
        </>
    );
};

export default SelectBox;
